/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// React
import { Suspense, use, useMemo, useState, type ComponentProps, type ReactElement, type ReactNode } from 'react';

// React Router
import { Link } from '@/components/link';

// Commerce SDK
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';

// Components
import Badge from '@/components/badge';
import { Spinner } from '@/components/spinner';
import { Typography } from '@/components/typography';
import HtmlFragment from '@/components/html-fragment';
import CartQuantityPicker from '@/components/cart/cart-quantity-picker';
import BundledProductItems from './bundled-product-items';
import ProductPrice from '../product-price';
import CartLinePromoCallouts from '../product-price/cart-line-promo-callouts';
import { getPriceData } from '../product-price/utils';
import CartLineItemAddOnRow from '@/components/cart/cart-line-item-add-on-row';
import { CartExtendToggle } from '@/components/cart/cart-extend-toggle';
import CartLineItemProductIncludesCard, {
    type CartLineItemIncludesPart,
} from '@/components/cart/cart-line-item-product-includes-card';
// TODO: uncomment after integrate gift basket api
// import { Label } from '@/components/forms/label';

// Hooks
import { useItemFetcherLoading } from '@/hooks/use-item-fetcher';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

// Utils
import { formatCurrency } from '@/lib/currency';
import { createProductUrl, getDisplayVariationValues, type EnrichedProductItem } from '@/lib/product/product-utils';
import { getResolvedProductImage } from '@/lib/product/resolved-image';
import { IMAGE_PRESETS } from '@/lib/image-presets';
import { type DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { getMattressProtectorRowState } from '@/lib/mattress-protector-utils';
import { useMattressProtectorToggle } from '@/hooks/use-mattress-protector-toggle';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { getEffectiveStockLevel } from '@/lib/product/inventory-utils';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { DynamicImage } from '@/components/dynamic-image';
import CartDeliveryEstimate from '@/components/delivery-estimate/cart-delivery-estimate';
import { TriangleAlert } from 'lucide-react';
import { resolveAtpDeliveryMessage } from '@/lib/atp/atp-delivery-message';

interface ProductIncludesItem extends CartLineItemIncludesPart {
    id: string;
}

function getProductIncludesFromCustomField(
    productItem: EnrichedProductItem,
    fallbackLabel: string
): ProductIncludesItem[] {
    const customIncludes = (productItem as Record<string, unknown>).c_productIncludes;
    if (!customIncludes) return [];

    const normalized = typeof customIncludes === 'string' ? JSON.parse(customIncludes) : customIncludes;
    if (!Array.isArray(normalized)) return [];

    const includes: ProductIncludesItem[] = [];
    normalized.forEach((item, index) => {
        if (typeof item === 'string') {
            includes.push({
                id: `${productItem.itemId || 'line-item'}-custom-include-${index}`,
                label: item,
            });
            return;
        }

        if (item && typeof item === 'object') {
            const value = item as Record<string, unknown>;
            const quantity =
                typeof value.quantity === 'number' || typeof value.quantity === 'string' ? value.quantity : undefined;
            includes.push({
                id: String(value.id || `${productItem.itemId || 'line-item'}-custom-include-${index}`),
                label: String(value.name || value.label || fallbackLabel),
                quantity,
            });
        }
    });

    return includes;
}

function getProductIncludesFromBundledProducts(
    productItem: EnrichedProductItem,
    fallbackLabel: string
): ProductIncludesItem[] {
    const rawProduct = productItem as Record<string, unknown>;
    const bundledProductItems = rawProduct.bundledProductItems;
    if (Array.isArray(bundledProductItems)) {
        return bundledProductItems.map((bundledItem, index) => {
            const value = bundledItem as Record<string, unknown>;
            return {
                id: String(value.itemId || `${productItem.itemId || 'line-item'}-bundled-include-${index}`),
                label: String(value.productName || value.name || fallbackLabel),
                quantity: typeof value.quantity === 'number' ? value.quantity : undefined,
            };
        });
    }

    if (!Array.isArray(productItem.bundledProducts)) return [];

    return productItem.bundledProducts.map((bundledProduct, index) => ({
        id: String(bundledProduct.product.id || `${productItem.itemId || 'line-item'}-bundled-include-${index}`),
        label: bundledProduct.product.name || fallbackLabel,
        quantity: bundledProduct.quantity,
    }));
}

/**
 * ProductItemVariantImage component that renders product images with fallback
 *
 * @param props - Component props
 * @param props.product - Product data containing image information
 * @param props.className - Optional CSS class name
 * @returns JSX element with product image or placeholder
 */
export function ProductItemVariantImage({
    productItem,
    className = '',
    widths = IMAGE_PRESETS.CART_LINE.widths as DynamicImageDimensions,
}: {
    productItem: EnrichedProductItem;
    className?: string;
    /**
     * Responsive widths array passed to `<DynamicImage>` to build a srcset. Each entry
     * is a pixel width that DIS will resize to, giving the browser multiple sources to
     * choose from based on layout size and device pixel ratio.
     *
     * Defaults to `IMAGE_PRESETS.CART_LINE.widths` ([120, 160]) which covers the standard
     * cart-row slot (w-28 = 112px mobile, w-40 = 160px desktop). Pass a tighter array for
     * compact surfaces — e.g. `[128]` for the summary variant (`w-16`, 64px@2×) or `[64]`
     * for order-review thumbnails.
     */
    widths?: DynamicImageDimensions;
}): ReactElement {
    const { t: tProduct } = useTranslation('product');

    if (!productItem) {
        return (
            <div className={cn('bg-product-image rounded-none flex-shrink-0 w-16', className)}>
                <div className="w-full h-full bg-product-image rounded-none" />
            </div>
        );
    }

    const image = getResolvedProductImage(productItem);
    const imageAltFallback = productItem?.productName || productItem?.name || tProduct('imageAlt') || 'Product Image';

    // Derive a single intrinsic size from the largest numeric entry in widths so the browser
    // can reserve square space before the image loads (CLS prevention). Falls back to 160 when
    // widths contains only vw strings or is empty.
    const numericWidths = (Array.isArray(widths) ? widths : []).filter((w): w is number => typeof w === 'number');
    const intrinsicSize = numericWidths.length > 0 ? Math.max(...numericWidths) : 160;

    return (
        <div
            className={cn(
                'bg-product-image rounded-sm flex-shrink-0 flex items-center justify-center aspect-square overflow-hidden',
                className
            )}>
            {image ? (
                <DynamicImage
                    src={image.link}
                    alt={image.alt || imageAltFallback}
                    widths={widths}
                    heights={widths}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    imageProps={{ width: intrinsicSize, height: intrinsicSize, decoding: 'async' }}
                />
            ) : (
                <div className="w-full h-full bg-product-image rounded-none" />
            )}
        </div>
    );
}

/**
 * ProductItemVariantName component that renders product name as a link
 *
 * @param props - Component props
 * @param props.product - Product data containing name and ID information
 * @returns JSX element with product name link
 */
export function ProductItemVariantName({ productItem }: { productItem: EnrichedProductItem }): ReactElement {
    const { t: tCart } = useTranslation('cart');
    const { t: tProduct } = useTranslation('product');
    if (!productItem) {
        return <div className="text-sm font-medium">{tCart('product.defaultName') || 'Product Name'}</div>;
    }

    const productId = productItem?.master?.masterId || productItem?.id;
    const productName = productItem?.productName || productItem?.name || tCart('product.defaultName') || 'Product Name';

    const isBonusProduct = Boolean(productItem?.bonusProductLineItem);
    return (
        <div className="mb-1.5 flex min-w-0 items-start gap-2">
            {isBonusProduct && (
                <Badge
                    variant="primary"
                    className="rounded-none"
                    role="status"
                    aria-label={tProduct('bonusProductAriaLabel')}>
                    {tProduct('bonusProduct')}
                </Badge>
            )}
            <div className="min-w-0 flex-1">
                <Link
                    to={createProductUrl(productId, null, 'color', null, productName)}
                    className="block break-words text-sm md:text-base font-bold leading-6 text-foreground"
                    title={productName}>
                    {productName}
                </Link>
            </div>
        </div>
    );
}

/**
 * ProductItemVariantAttributes component that displays product variation attributes
 *
 * @param props - Component props
 * @param props.product - Product data containing variation information
 * @param props.displayVariant - Display variant to control quantity display
 * @param props.promotions - Promotions data by ID
 * @returns JSX element with variation attributes or fallback
 */
export function ProductItemVariantAttributes({
    productItem,
    displayVariant = 'default',
}: {
    productItem: EnrichedProductItem;
    displayVariant?: 'default' | 'summary';
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
}): ReactElement {
    const { t } = useTranslation('cart');
    // Memoize expensive calculations
    const displayVariationValues = useMemo(
        () => getDisplayVariationValues(productItem?.variationAttributes, productItem?.variationValues),
        [productItem?.variationAttributes, productItem?.variationValues]
    );

    const variationEntries = Object.entries(displayVariationValues);

    return (
        <div>
            {/* Quantity - only show in summary variant */}
            {displayVariant === 'summary' && (
                <div className="text-xs md:text-sm text-muted-foreground mt-1.5">
                    {t('attributes.quantity')} {productItem.quantity || 1}
                </div>
            )}

            {/* Item ID — first attribute row */}
            {(productItem?.productId ?? productItem?.id) ? (
                <div className="mt-1.5 text-xs md:text-sm text-muted-foreground">
                    {t('attributes.itemId')} · {productItem.productId ?? productItem.id}
                </div>
            ) : null}

            {/* Variation Attributes */}
            {variationEntries.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-muted-foreground">
                    {variationEntries.map(([name, value], index) => (
                        <div key={name} className="inline-flex items-center gap-1.5">
                            <span>{value}</span>
                            {index < variationEntries.length - 1 ? <span className="text-xs">•</span> : null}
                        </div>
                    ))}
                    {/* Keep attribute names for accessibility context */}
                    <span className="sr-only">
                        {variationEntries.map(([name, value]) => `${name}: ${value}`).join(', ')}
                    </span>
                </div>
            )}

            {/* Promotions Info - shown in summary variant only, moved to right column for default */}
            {displayVariant === 'summary' && <ProductItemPromotions productItem={productItem} />}
        </div>
    );
}

/**
 * ProductItemPromotions component that displays promotion info for a product item
 */
export function ProductItemPromotions({
    productItem,
    className,
    /** Full-width row with badge aligned to the end (cart line / price column). */
    alignEnd = false,
}: {
    productItem: EnrichedProductItem;
    className?: string;
    alignEnd?: boolean;
}): ReactElement | null {
    const { t: tMiniCart, i18n } = useTranslation('miniCart');
    const { currency } = useSite();

    const isBonusProduct = Boolean(productItem?.bonusProductLineItem);
    if (isBonusProduct) return null;

    const { listPrice, currentPrice } = getPriceData(productItem);
    if (!listPrice) return null;
    const discount = (listPrice - currentPrice) * (productItem?.quantity ?? 1);
    if (discount <= 0) return null;

    const badge = (
        <Badge
            className={cn(
                'h-auto min-h-0 rounded-none border-0 bg-muted px-1.5 py-0.5 text-xs font-semibold leading-4 text-secondary-foreground whitespace-normal break-words',
                className
            )}>
            {tMiniCart('saved', {
                amount: formatCurrency(discount, i18n.language, currency),
            })}
        </Badge>
    );

    if (alignEnd) {
        return <div className="flex w-full justify-end">{badge}</div>;
    }

    return badge;
}

/**
 * Resolves `promotionsPromise` via `use()` and passes the resulting map to
 * `CartLinePromoCallouts`. Must be rendered inside a `<Suspense>` boundary so
 * the line item row never blocks on the Promotions API response.
 */
function DeferredCartLinePromoCallouts({
    promotionsPromise,
    ...props
}: ComponentProps<typeof CartLinePromoCallouts> & {
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}) {
    const promotions = promotionsPromise ? use(promotionsPromise) : undefined;
    return <CartLinePromoCallouts promotions={promotions} {...props} />;
}

/**
 * Props for the ProductItem component
 *
 * @interface ProductItemProps
 * @property {Product | undefined} product - Combined basket item and product data
 * @property {'default' | 'summary'} [displayVariant] - Display variant: 'default' for full, 'summary' for compact
 * @property {Record<string, ShopperPromotions.schemas['Promotion']>} [promotions] - Promotions data by ID
 * @property {function} [primaryAction] - Render prop function to create primary actions
 * @property {function} [secondaryActions] - Render prop function to create secondary actions
 * @property {function} [deliveryActions] - Render prop for per-line fulfillment (e.g. BOPIS pickup/delivery dropdown)
 * @property {ReactNode} [lineItemExtra] - Optional pre-rendered content for the end of the cart line right column (e.g. gift checkbox)
 */
interface ProductItemProps {
    productItem: EnrichedProductItem | undefined;
    displayVariant?: 'default' | 'summary';
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
    /**
     * Non-critical promotions data promise. When provided, promo callouts on the
     * line item stream in asynchronously under their own Suspense boundary so the
     * line item row renders immediately without waiting on the Promotions API.
     */
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    primaryAction?: (productItem: EnrichedProductItem) => ReactElement | undefined;
    secondaryActions?: (productItem: EnrichedProductItem) => ReactElement | undefined;
    deliveryActions?: (productItem: EnrichedProductItem) => ReactElement | undefined;
    /** Pre-rendered extra content at the end of the cart line right column (e.g. gift checkbox) */
    lineItemExtra?: ReactNode;
    bonusDiscountLineItems?: ShopperBasketsV2.schemas['BonusDiscountLineItem'][];
    maxBonusQuantity?: number;
    /**
     * Optional render prop that receives the enriched product item and returns
     * content rendered directly below the product item row.
     * Used as a pass-through slot for sibling stories (CART-54 Extend,
     * CART-55 Mattress Protector) to inject inline add-on offers without
     * modifying this component's own rendering logic.
     */
    addOnSlot?: (product: EnrichedProductItem) => ReactNode;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    /** Whether this item is a pickup item (affects stock level calculation) */
    isPickup?: boolean;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
}

/**
 * ProductItem component that displays individual product information in cart or summary views
 *
 * This component handles:
 * - Product image display with fallback
 * - Product name as clickable link
 * - Variation attributes display
 * - Price formatting and display
 * - Primary and secondary actions
 * - Responsive layout for mobile/desktop
 * - Summary and default display variants
 * - Loading states with skeleton overlay
 *
 * @param props - Component props
 * @returns JSX element representing the product item
 */
function ProductItem({
    productItem,
    displayVariant = 'default',
    promotions,
    promotionsPromise,
    primaryAction,
    secondaryActions,
    deliveryActions,
    bonusDiscountLineItems,
    maxBonusQuantity,
    addOnSlot,
    lineItemExtra,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    isPickup = false,
}: ProductItemProps): ReactElement {
    // Track loading state for all fetchers related to this item
    const isItemFetcherLoading = useItemFetcherLoading(productItem?.itemId);
    // Get currency from context (automatically derived from locale)
    const { currency } = useSite();
    const { t, i18n } = useTranslation();
    const { t: tDelivery } = useTranslation('delivery');
    const config = useConfig();
    const showLineItemDescription = config.pages.cart?.showLineItemDescription ?? false;
    const includesFallbackItem = t('miniCart:productIncludesFallbackItem') || 'Included item';

    // CART-09: project mattress-protector enrichment fields into the
    // checkbox-row's UI props. `visible=false` when this item isn't an
    // eligible mattress, which gates the row away entirely.
    const mattressProtectorRow = useMemo(
        () => getMattressProtectorRowState(productItem, i18n.language, currency),
        [productItem, i18n.language, currency]
    );
    // Interactive checked / disabled / onCheckedChange — owns the
    // add/remove SCAPI calls and optimistic UI for THIS row.
    const mattressProtectorToggle = useMattressProtectorToggle(productItem);
    const productIncludes = useMemo(() => {
        try {
            const fromCustomField = getProductIncludesFromCustomField(
                productItem as EnrichedProductItem,
                includesFallbackItem
            );
            if (fromCustomField.length > 0) return fromCustomField;
        } catch {
            // Ignore parse errors and fallback to bundled data for now.
        }

        return getProductIncludesFromBundledProducts(productItem as EnrichedProductItem, includesFallbackItem);
    }, [productItem, includesFallbackItem]);

    // Check if this is a bonus product
    const isBonusProduct = Boolean(productItem?.bonusProductLineItem);

    // Determine if this is a choice-based bonus product by checking bonusDiscountLineItems
    // Must be called before any early returns (React Hooks rules)
    const isChoiceBasedBonusProduct = useMemo(() => {
        if (!productItem || !isBonusProduct || !productItem.bonusDiscountLineItemId || !bonusDiscountLineItems) {
            return false;
        }
        const matchingLineItem = bonusDiscountLineItems.find((item) => item.id === productItem.bonusDiscountLineItemId);
        // Choice-based bonus products have a bonusProducts array in the discount line item
        return Boolean(matchingLineItem?.bonusProducts && matchingLineItem.bonusProducts.length > 0);
    }, [productItem, isBonusProduct, bonusDiscountLineItems]);

    // Enrich each price adjustment with calloutMsg from the matching productPromotion when the
    // basket's priceAdjustment itself doesn't carry one. SFCC sometimes omits calloutMsg on
    // adjustments while populating it on the item-level productPromotions array. The enriched
    // value flows into CartLinePromoCallouts so the customer-facing message is shown instead of
    // falling back to itemText (which may be the raw coupon code).
    const enrichedPriceAdjustments = useMemo(() => {
        if (!productItem?.priceAdjustments?.length) return productItem?.priceAdjustments;
        const promoCallouts: Record<string, string> = {};
        for (const pp of productItem.productPromotions ?? []) {
            if (pp.promotionId && pp.calloutMsg) {
                promoCallouts[pp.promotionId] = pp.calloutMsg;
            }
        }
        return productItem.priceAdjustments.map((adj) => {
            if (adj.calloutMsg || !adj.promotionId) return adj;
            const calloutMsg = promoCallouts[adj.promotionId];
            return calloutMsg ? { ...adj, calloutMsg } : adj;
        });
    }, [productItem?.priceAdjustments, productItem?.productPromotions]);

    const isAutoBonusProduct = isBonusProduct && !isChoiceBasedBonusProduct;
    const showProductIncludesFallback = !isBonusProduct && productIncludes.length === 0;
    const hasExplicitInventoryMessage =
        typeof productItem?.inventoryMessage === 'string' && productItem.inventoryMessage.trim().length > 0;
    const isUnavailableItem =
        productItem?.isProductUnavailable === true ||
        productItem?.orderable === false ||
        productItem?.inventory?.orderable === false;
    const shouldShowInventoryAlert = Boolean(productItem?.showInventoryMessage) || isUnavailableItem;
    const inventoryAlertMessage = hasExplicitInventoryMessage
        ? (productItem.inventoryMessage as string)
        : t('cart.itemUnavailableMessage', {
              defaultValue: 'This item is currently unavailable. Please remove from cart or save for later.',
          });

    // Stock validation error lifted from CartQuantityPicker via onValidationErrorChange callback.
    const [stockValidationError, setStockValidationError] = useState<string | null>(null);

    // ATP delivery availability — used to show an alert below the Remove button when the
    // item is ATP-unavailable but NOT already flagged by the basket as unorderable.
    const atpMessage = resolveAtpDeliveryMessage(productItem);
    const isAtpUnavailable = !shouldShowInventoryAlert && atpMessage?.variant === 'unavailable';

    // Determine stock level: site-level ATS by default, store-specific for pickup items
    let stockLevel = productItem?.inventory?.ats;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    if (isPickup && productItem) {
        stockLevel = getEffectiveStockLevel({
            product: productItem as unknown as ShopperProducts.schemas['Product'],
            isPickup: true,
            storeInventoryId: productItem.inventoryId,
        });
    }
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    if (!productItem || typeof productItem !== 'object') {
        return <div data-testid="product-item-error">Product data not available</div>;
    }

    // Summary variant - compact display for product summary
    if (displayVariant === 'summary') {
        return (
            <div
                className="grid md:grid-cols-[112px_1fr] grid-cols-[72px_1fr] gap-4"
                data-testid={`sf-product-item-summary-${productItem?.productId || productItem?.id}`}>
                <div className="flex items-center justify-center">
                    {/* Summary-variant slot is `w-16` (64px) — request 128 for retina. */}
                    <ProductItemVariantImage productItem={productItem} className="w-16" />
                </div>
                <div className="flex-1 space-y-1 min-w-0">
                    <ProductItemVariantName productItem={productItem} />
                    {productItem.bundledProducts && productItem.bundledProducts.length > 0 && (
                        <BundledProductItems bundledProducts={productItem.bundledProducts} />
                    )}
                    <ProductItemVariantAttributes
                        productItem={productItem}
                        displayVariant={displayVariant}
                        promotions={promotions}
                    />
                    <ProductPrice
                        type="unit"
                        product={productItem as ShopperProducts.schemas['Product']}
                        currency={currency}
                        labelForA11y={productItem?.productName}
                        currentPriceProps={{
                            className: 'text-right font-semibold text-sm leading-none relative',
                        }}
                        listPriceProps={{
                            className: 'text-muted-foreground text-right text-sm leading-none relative',
                        }}
                        className="text-sm"
                    />
                </div>
            </div>
        );
    }
    const lineItemExtraContent = !isAutoBonusProduct ? lineItemExtra : undefined;

    // Default variant - full product item with card styling
    return (
        <>
            <div className="relative" data-testid={`sf-product-item-${productItem?.productId || productItem?.id}`}>
                <div className="bg-card text-card-foreground border-0 shadow-none rounded-none">
                    <div className="relative overflow-hidden px-0 py-6 md:px-0 md:py-8">
                        <div className="grid min-w-0 grid-cols-[112px_minmax(0,1fr)] gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:gap-6">
                            <div className="flex-shrink-0 flex items-start justify-center">
                                {/* Product Image */}
                                <ProductItemVariantImage productItem={productItem} className="w-28 md:w-40" />
                            </div>

                            {/* Product Details */}
                            <div className="min-w-0 flex-1 space-y-3">
                                <div className="flex min-w-0 flex-wrap gap-4 md:gap-6">
                                    <div className="min-w-0 flex-1">
                                        <div className="hidden md:hidden float-right ml-2">
                                            {deliveryActions?.(productItem)}
                                        </div>
                                        <ProductItemVariantName productItem={productItem} />
                                        {productItem.bundledProducts && (
                                            <BundledProductItems bundledProducts={productItem.bundledProducts} />
                                        )}
                                        <div data-testid="desktop-product-price">
                                            {(productItem.priceAfterItemDiscount ?? productItem.price ?? 0) === 0 ? (
                                                <span className="text-[var(--cart-free-price)] text-[length:var(--cart-free-price-size)] font-semibold">
                                                    {t('miniCart:free')}
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap items-center gap-y-1.5 gap-x-2">
                                                    <ProductPrice
                                                        type="total"
                                                        product={productItem as ShopperProducts.schemas['Product']}
                                                        quantity={productItem.quantity ?? 1}
                                                        currency={currency}
                                                        labelForA11y={productItem?.productName}
                                                        className="flex flex-wrap items-center gap-2"
                                                        currentPriceProps={{
                                                            className: 'text-sm font-normal leading-5 relative',
                                                        }}
                                                        listPriceProps={{
                                                            className:
                                                                'text-muted-foreground text-sm font-normal leading-5 relative line-through',
                                                        }}
                                                        hidePromo
                                                        hasDiscount={!!productItem.priceAdjustments?.length}
                                                    />
                                                    <Suspense
                                                        fallback={
                                                            <CartLinePromoCallouts
                                                                priceAdjustments={enrichedPriceAdjustments}
                                                            />
                                                        }>
                                                        <DeferredCartLinePromoCallouts
                                                            priceAdjustments={enrichedPriceAdjustments}
                                                            promotionsPromise={promotionsPromise}
                                                        />
                                                    </Suspense>
                                                </div>
                                            )}
                                        </div>
                                        <ProductItemVariantAttributes
                                            productItem={productItem}
                                            displayVariant={displayVariant}
                                            promotions={promotions}
                                        />
                                        {showLineItemDescription && productItem.shortDescription ? (
                                            <Typography variant="muted" as="p" className="text-sm mt-2">
                                                {productItem.shortDescription}
                                            </Typography>
                                        ) : null}
                                        {showLineItemDescription &&
                                        !productItem.shortDescription &&
                                        productItem.longDescription ? (
                                            <HtmlFragment
                                                content={productItem.longDescription}
                                                contentType="plain-text"
                                                className="text-sm text-muted-foreground mt-2 leading-relaxed"
                                            />
                                        ) : null}

                                        <UITarget targetId="sfcc.cart.tax.lineItemMessage" />
                                    </div>
                                    <div className="grid content-start justify-items-start gap-3 shrink-0 md:w-[180px] md:justify-items-end md:gap-4">
                                        <div className="hidden">{deliveryActions?.(productItem)}</div>

                                        <div className="w-full">
                                            {/* Quantity Display/Selector */}
                                            <CartQuantityPicker
                                                value={String(productItem.quantity)}
                                                itemId={productItem.itemId || ''}
                                                stockLevel={stockLevel}
                                                max={isBonusProduct ? maxBonusQuantity : undefined}
                                                disabled={isAutoBonusProduct}
                                                onValidationErrorChange={setStockValidationError}
                                            />
                                        </div>
                                        {(productItem.quantity ?? 1) > 1 ? (
                                            <Typography variant="small" className="text-muted-foreground text-xs">
                                                {`${formatCurrency(
                                                    (productItem.priceAfterItemDiscount ?? productItem.price ?? 0) /
                                                        (productItem.quantity ?? 1),
                                                    i18n.language,
                                                    currency
                                                )}/each`}
                                            </Typography>
                                        ) : null}
                                        <UITarget targetId="sfcc.cart.shipping.deliveryEstimate">
                                            <CartDeliveryEstimate
                                                productItem={productItem}
                                                className="md:text-right"
                                                hideUnavailable
                                            />
                                        </UITarget>
                                        {lineItemExtraContent && (
                                            <div className="flex w-full min-w-0 shrink-0 justify-end">
                                                {lineItemExtraContent}
                                            </div>
                                        )}
                                    </div>
                                    {!isAutoBonusProduct && primaryAction && (
                                        <div className="min-w-0 w-full">
                                            <div data-testid="mobile-primary-action">{primaryAction(productItem)}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 w-full space-y-3">
                                    {!isAutoBonusProduct && secondaryActions && (
                                        <div className="w-full">{secondaryActions(productItem)}</div>
                                    )}

                                    {/* Priority 1: item unavailable (orderable=false / showInventoryMessage) */}
                                    {shouldShowInventoryAlert && (
                                        <div className="flex items-start gap-2 text-sm text-destructive">
                                            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                                            <span className="break-words">{inventoryAlertMessage}</span>
                                        </div>
                                    )}

                                    {/* Priority 2: ATP unavailable — only when item-unavailable is not showing */}
                                    {isAtpUnavailable && (
                                        <div className="flex items-start gap-2 text-sm text-destructive">
                                            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                                            <span className="break-words">{tDelivery('unavailable')}</span>
                                        </div>
                                    )}

                                    {/* Priority 3: stock qty validation — only when item is orderable */}
                                    {!shouldShowInventoryAlert && stockValidationError && (
                                        <div
                                            className="flex items-start gap-2 text-sm text-destructive"
                                            role="alert"
                                            aria-live="polite">
                                            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                                            <span className="break-words">{stockValidationError}</span>
                                        </div>
                                    )}

                                    {/* INT-02 — Full-width Extend protection-plan add-on row.
                                    Per Figma the toggle sits below the inline Save-for-Later /
                                    Remove buttons and above the mattress-protector add-on row.
                                    The component self-hides on warranty PLIs, when the productId
                                    has no live `POST /offers/cart` offer, or when the cart route's
                                    `<ExtendCartOffersProvider>` isn't mounted (mini-cart, order
                                    summary, etc.). Eligibility is driven entirely by the live
                                    response — cached `c_protectionPlanEligible` is not consulted. */}
                                    {!isBonusProduct ? (
                                        <CartExtendToggle product={productItem} className="w-full" />
                                    ) : null}
                                    {/* CART-09 mattress-protector offer row.
                                    Visibility & price are derived from the SCAPI basket hook's
                                    `c_mattressProtector*` enrichment fields. Checked state and
                                    the toggle handler come from `useMattressProtectorToggle`,
                                    which submits to the dedicated server action and applies
                                    optimistic UI from `fetcher.formData`. */}
                                    {!isBonusProduct && mattressProtectorRow.visible ? (
                                        <CartLineItemAddOnRow
                                            id={`cart-add-on-${productItem.itemId || productItem.productId || 'line-item'}`}
                                            size="compact"
                                            className="w-full"
                                            label={t('miniCart:addOnLabel')}
                                            priceText={mattressProtectorRow.priceText}
                                            checked={mattressProtectorToggle.checked}
                                            disabled={mattressProtectorToggle.disabled}
                                            onCheckedChange={mattressProtectorToggle.onCheckedChange}
                                        />
                                    ) : null}
                                    {productIncludes.length > 0 ? (
                                        <CartLineItemProductIncludesCard
                                            className="hidden w-full"
                                            title={t('miniCart:productIncludesTitle')}
                                            items={productIncludes.map((item) => ({
                                                label: item.label,
                                                quantity: item.quantity,
                                            }))}
                                        />
                                    ) : null}
                                    {showProductIncludesFallback ? (
                                        <CartLineItemProductIncludesCard
                                            className="hidden w-full"
                                            title={t('miniCart:productIncludesTitle')}
                                            items={[
                                                {
                                                    label: t('miniCart:productIncludesFallbackItem'),
                                                },
                                            ]}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        {/* Loading Spinner Overlay */}
                        {isItemFetcherLoading && (
                            <div
                                className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 pointer-events-none flex items-center justify-center"
                                data-testid={`sf-product-item-loading-${productItem.productId || productItem.id}`}>
                                <Spinner size="lg" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {addOnSlot?.(productItem)}
        </>
    );
}

export default ProductItem;
